import { z } from "zod";
import { paginationMetaDtoSchema } from "@nasam/kernel";

const nonEmptyString = z.string().min(1);

export const accountTypeDtoSchema = z.enum(["COMPANY", "INDIVIDUAL"]);

export const currencyDtoSchema = z.object({
  id: nonEmptyString,
  code: nonEmptyString,
  nameEn: nonEmptyString,
  nameAr: nonEmptyString,
  symbol: nonEmptyString,
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export const companyAttachmentDtoSchema = z.object({
  id: nonEmptyString,
  fileUrl: nonEmptyString,
  extension: nonEmptyString,
  createdAt: nonEmptyString,
  updatedAt: nonEmptyString,
  createdById: nonEmptyString,
  updatedById: z.string().nullable(),
});

export const companyAdminSummaryDtoSchema = z.object({
  id: nonEmptyString,
  nameEn: nonEmptyString,
  nameAr: nonEmptyString,
  email: z.email(),
  username: nonEmptyString,
  isAccountAdmin: z.boolean().optional(),
  createdAt: nonEmptyString,
  updatedAt: nonEmptyString,
});

export const companyDtoSchema = z.object({
  id: nonEmptyString,
  type: accountTypeDtoSchema,
  nameEn: nonEmptyString,
  nameAr: nonEmptyString,
  address: z.string().nullable(),
  remarks: z.string().nullable(),
  email: z.email(),
  phoneNumber1: nonEmptyString,
  phoneNumber2: z.string().nullable(),
  fax: z.string().nullable(),
  createdAt: nonEmptyString,
  updatedAt: nonEmptyString,
  currencyId: nonEmptyString,
  currency: currencyDtoSchema.optional(),
  // Present on GET; on `PATCH` only when new files were uploaded this
  // request — callers re-fetch via getById when they need the current list.
  attachments: z.array(companyAttachmentDtoSchema).optional(),
  // Present on GET, absent on the create/update response.
  admin: companyAdminSummaryDtoSchema.nullable().optional(),
  isActive: z.boolean().optional(),
  isFullySubscribed: z.boolean().optional(),
  subscriptionId: z.string().nullable().optional(),
});

export const companySummaryDtoSchema = z.object({
  totalAccounts: z.number().int().nonnegative(),
  totalActiveAccounts: z.number().int().nonnegative(),
  totalInactiveAccounts: z.number().int().nonnegative(),
  totalAccountsAdmins: z.number().int().nonnegative(),
});

export const accountReferenceDtoSchema = z.object({
  id: nonEmptyString,
  nameEn: nonEmptyString,
  nameAr: nonEmptyString,
});

export const accountAdminStatusDtoSchema = z.enum([
  "ACTIVE",
  "INACTIVE",
  "PENDING_ACTIVATION",
]);

export const accountAdminDtoSchema = z.object({
  id: nonEmptyString,
  username: nonEmptyString,
  email: z.email(),
  nameEn: nonEmptyString,
  nameAr: nonEmptyString,
  phoneNumber: nonEmptyString,
  enable2FA: z.boolean(),
  status: accountAdminStatusDtoSchema,
  createdAt: nonEmptyString,
  updatedAt: nonEmptyString,
  accountId: nonEmptyString,
  typeId: z.union([z.string(), z.number()]).optional(),
  type: z
    .object({ id: z.union([z.string(), z.number()]), slug: nonEmptyString })
    .optional(),
  account: accountReferenceDtoSchema.optional(),
});

export const companyListResponseDtoSchema = z.object({
  data: z.array(companyDtoSchema),
  meta: paginationMetaDtoSchema,
});

// `GET /accounts/archived` — each row carries only what the archived-companies
// screen shows (name, admin, email) plus `archivedAt`.
export const archivedCompanyListItemDtoSchema = z.object({
  id: nonEmptyString,
  nameEn: nonEmptyString,
  nameAr: nonEmptyString,
  email: z.email(),
  admin: z
    .object({
      id: nonEmptyString,
      nameEn: nonEmptyString,
      nameAr: nonEmptyString,
      email: z.email(),
      username: nonEmptyString,
    })
    .nullable(),
  archivedAt: nonEmptyString,
});

export const archivedCompanyListResponseDtoSchema = z.object({
  data: z.array(archivedCompanyListItemDtoSchema),
  meta: paginationMetaDtoSchema,
});

export const companyResponseDtoSchema = z.object({
  data: companyDtoSchema,
  message: z.string().optional(),
});

// `POST /accounts` answers 201 for two outcomes. A created account carries a
// full `companyDtoSchema`; when the submitted details match an already-archived
// account the backend creates nothing and returns only this trimmed record plus
// a localized "restore this company?" prompt. The missing `type`/`createdAt`/
// `currencyId` is what lets the repository tell the two apart.
export const archivedCompanyMatchDtoSchema = z.object({
  id: nonEmptyString,
  nameEn: nonEmptyString,
  nameAr: nonEmptyString,
  email: z.email(),
  phoneNumber1: nonEmptyString,
});

export const archivedCompanyMatchResponseDtoSchema = z.object({
  data: archivedCompanyMatchDtoSchema,
  message: nonEmptyString,
});
export const companySummaryResponseDtoSchema = z.object({
  data: companySummaryDtoSchema,
});
export const deleteCompanyResponseDtoSchema = z.object({
  data: z.object({
    accountsCount: z.number().int().nonnegative(),
    usersCount: z.number().int().nonnegative(),
  }),
});

export const accountAdminListResponseDtoSchema = z.object({
  data: z.array(accountAdminDtoSchema),
  meta: paginationMetaDtoSchema,
});

// `GET /users/accounts-admins/archived` — each row carries only what the
// archived-admins screen shows, plus `archivedAt`.
export const archivedAdminListItemDtoSchema = z.object({
  id: nonEmptyString,
  username: nonEmptyString,
  email: z.email(),
  nameEn: nonEmptyString,
  nameAr: nonEmptyString,
  account: accountReferenceDtoSchema.nullable(),
  archivedAt: nonEmptyString,
});

export const archivedAdminListResponseDtoSchema = z.object({
  data: z.array(archivedAdminListItemDtoSchema),
  meta: paginationMetaDtoSchema,
});

export const accountAdminResponseDtoSchema = z.object({
  data: accountAdminDtoSchema,
  message: z.string().optional(),
});

// `POST /users/account-admin` mirrors `POST /accounts`: 201 for a real creation
// (full `accountAdminDtoSchema`) or for a match against an already-archived
// admin, which creates nothing and returns only this trimmed record plus a
// localized "restore this admin?" prompt. Identity fields beyond `id` are
// optional so any trimmed subset the backend sends still parses.
export const archivedAdminMatchDtoSchema = z.object({
  id: nonEmptyString,
  username: nonEmptyString.optional(),
  email: z.email().optional(),
  nameEn: nonEmptyString.optional(),
  nameAr: nonEmptyString.optional(),
});

export const archivedAdminMatchResponseDtoSchema = z.object({
  data: archivedAdminMatchDtoSchema,
  message: nonEmptyString,
});

// `PATCH /users/accounts-admins/:id/restore` returns a full admin (the shape of
// `accountAdminResponseDtoSchema`) plus a localized `message` — reuse that.

export const companyCreateWriteDtoSchema = z.object({
  type: accountTypeDtoSchema.optional(),
  nameEn: nonEmptyString,
  nameAr: nonEmptyString,
  address: z.string().optional(),
  remarks: z.string().optional(),
  email: z.email(),
  phoneNumber1: nonEmptyString,
  phoneNumber2: z.string().optional(),
  fax: z.string().optional(),
  currencyId: z.string().optional(),
});
export const companyUpdateWriteDtoSchema = companyCreateWriteDtoSchema
  .partial()
  .extend({
    attachmentsIdsToBeDeleted: z.array(nonEmptyString).optional(),
  });

export const accountAdminCreateWriteDtoSchema = z.object({
  accountId: nonEmptyString,
  nameEn: nonEmptyString,
  nameAr: nonEmptyString,
  username: nonEmptyString,
  email: z.email(),
  phoneNumber: nonEmptyString,
});
export const accountAdminUpdateWriteDtoSchema = z.object({
  nameEn: nonEmptyString.optional(),
  nameAr: nonEmptyString.optional(),
  email: z.email().optional(),
  phoneNumber: nonEmptyString.optional(),
  status: z.enum(["ACTIVE", "INACTIVE"]).optional(),
});

export const resendActivationLinkWriteDtoSchema = z.object({
  email: z.email(),
});

export const messageResponseDtoSchema = z.object({ message: nonEmptyString });

// Every branch endpoint (list/getById/create/update/restore) sends latitude/
// longitude flat on the branch itself -- there's no nested `location` object
// on the wire; branchFromDto reshapes it into one for the domain model.
export const branchDtoSchema = z.object({
  id: nonEmptyString,
  nameEn: nonEmptyString,
  nameAr: nonEmptyString,
  isMain: z.boolean(),
  isActive: z.boolean(),
  // Null on a main branch, which has no location until someone sets one.
  countryId: z.string().nullable().optional(),
  governorateId: z.string().nullable().optional(),
  country: accountReferenceDtoSchema.nullable().optional(),
  governorate: accountReferenceDtoSchema.nullable().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  createdAt: nonEmptyString,
  updatedAt: nonEmptyString,
  accountId: nonEmptyString,
  createdById: nonEmptyString.nullable().optional(),
  updatedById: nonEmptyString.nullable().optional(),
});

export const branchCreateWriteDtoSchema = z.object({
  accountId: nonEmptyString.optional(),
  nameEn: nonEmptyString,
  nameAr: nonEmptyString,
  // Optional -- a branch can be created with no location yet; sent as a
  // pair (both or neither), same for latitude/longitude.
  countryId: nonEmptyString.optional(),
  governorateId: nonEmptyString.optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
});

export const branchUpdateWriteDtoSchema = z.object({
  nameEn: nonEmptyString.optional(),
  nameAr: nonEmptyString.optional(),
  countryId: nonEmptyString.optional(),
  governorateId: nonEmptyString.optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  isActive: z.boolean().optional(),
});

export const branchResponseDtoSchema = z.object({
  message: nonEmptyString.optional(),
  data: branchDtoSchema,
});

export const branchListResponseDtoSchema = z.object({
  data: z.array(branchDtoSchema),
  meta: paginationMetaDtoSchema,
});

export const archivedBranchListItemDtoSchema = z.object({
  id: nonEmptyString,
  nameEn: nonEmptyString,
  nameAr: nonEmptyString,
  isActive: z.boolean(),
  account: accountReferenceDtoSchema,
  archivedAt: nonEmptyString,
});

export const archivedBranchListResponseDtoSchema = z.object({
  data: z.array(archivedBranchListItemDtoSchema),
  meta: paginationMetaDtoSchema,
});

export const archivedBranchMatchDtoSchema = z.object({
  id: nonEmptyString,
  nameEn: nonEmptyString,
  nameAr: nonEmptyString,
  accountId: nonEmptyString,
  deletedAt: nonEmptyString,
});

export const archivedBranchMatchResponseDtoSchema = z.object({
  data: archivedBranchMatchDtoSchema,
  message: nonEmptyString,
});

// The boundary is sent/received as an open ring of flat lat/lng points --
// no GeoJSON, no repeated closing point. regionFromDto/regionCreateToDto
// reshape it into/out of the domain's closed-ring RegionPolygon.
export const regionBoundaryPointDtoSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
});

export const regionStatusDtoSchema = z.enum(["ACTIVE", "INACTIVE"]);

export const regionDtoSchema = z.object({
  id: nonEmptyString,
  nameEn: nonEmptyString,
  nameAr: nonEmptyString,
  boundary: z.array(regionBoundaryPointDtoSchema),
  status: regionStatusDtoSchema,
  createdAt: nonEmptyString,
  updatedAt: nonEmptyString,
  accountId: nonEmptyString,
  createdById: nonEmptyString.nullable().optional(),
  updatedById: nonEmptyString.nullable().optional(),
  // Absent on the create response; present everywhere else.
  account: accountReferenceDtoSchema.optional(),
});

export const regionListResponseDtoSchema = z.object({
  data: z.array(regionDtoSchema),
  meta: paginationMetaDtoSchema,
});

export const regionResponseDtoSchema = z.object({
  message: nonEmptyString.optional(),
  data: regionDtoSchema,
});

// No accountId (always the caller's own account) and no status (every new
// region starts ACTIVE) -- create is a strict subset of update.
export const regionCreateWriteDtoSchema = z.object({
  nameEn: nonEmptyString,
  nameAr: nonEmptyString,
  boundary: z.array(regionBoundaryPointDtoSchema),
});

export const regionUpdateWriteDtoSchema = z.object({
  nameEn: nonEmptyString.optional(),
  nameAr: nonEmptyString.optional(),
  boundary: z.array(regionBoundaryPointDtoSchema).optional(),
  status: regionStatusDtoSchema.optional(),
});

export const archivedRegionDtoSchema = regionDtoSchema.extend({
  archivedAt: nonEmptyString,
});

export const archivedRegionListResponseDtoSchema = z.object({
  data: z.array(archivedRegionDtoSchema),
  meta: paginationMetaDtoSchema,
});

export const archivedRegionMatchDtoSchema = z.object({
  id: nonEmptyString,
  nameEn: nonEmptyString,
  nameAr: nonEmptyString,
  accountId: nonEmptyString,
  deletedAt: nonEmptyString,
});

export const archivedRegionMatchResponseDtoSchema = z.object({
  data: archivedRegionMatchDtoSchema,
  message: nonEmptyString,
});

export const countryDtoSchema = z.object({
  id: nonEmptyString,
  code: nonEmptyString,
  nameEn: nonEmptyString,
  nameAr: nonEmptyString,
});

export const governorateDtoSchema = z.object({
  id: nonEmptyString,
  nameEn: nonEmptyString,
  nameAr: nonEmptyString,
  countryId: nonEmptyString,
});

// Every /departments endpoint returns this row shape. Departments are
// account-level; `account` is absent on the create response, and `branch`
// is a leftover of an older contract the UI shows only when present.
export const departmentDtoSchema = z.object({
  id: nonEmptyString,
  nameEn: nonEmptyString,
  nameAr: nonEmptyString,
  remarks: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: nonEmptyString,
  updatedAt: nonEmptyString,
  accountId: z.string().nullable().optional(),
  createdById: z.string().nullable().optional(),
  updatedById: z.string().nullable().optional(),
  account: accountReferenceDtoSchema.nullable().optional(),
  branch: z
    .object({
      id: nonEmptyString,
      nameEn: nonEmptyString,
      nameAr: nonEmptyString,
    })
    .nullable()
    .optional(),
});

export const departmentResponseDtoSchema = z.object({
  message: nonEmptyString.optional(),
  data: departmentDtoSchema,
});

export const departmentListResponseDtoSchema = z.object({
  data: z.array(departmentDtoSchema),
  meta: paginationMetaDtoSchema,
});

export const departmentCreateWriteDtoSchema = z.object({
  nameEn: nonEmptyString,
  nameAr: nonEmptyString,
  remarks: nonEmptyString.optional(),
});

// Partial: only what changed. `remarks: null` clears the remarks.
export const departmentUpdateWriteDtoSchema = z.object({
  nameEn: nonEmptyString.optional(),
  nameAr: nonEmptyString.optional(),
  isActive: z.boolean().optional(),
  remarks: nonEmptyString.nullable().optional(),
});

export const archivedDepartmentDtoSchema = departmentDtoSchema.extend({
  archivedAt: nonEmptyString,
});

export const archivedDepartmentListResponseDtoSchema = z.object({
  data: z.array(archivedDepartmentDtoSchema),
  meta: paginationMetaDtoSchema,
});

// The 409 body POST /departments sends when a name matches an archived
// department. The message is translated, so `code` is what identifies it.
export const archivedDepartmentMatchResponseDtoSchema = z.object({
  code: z.literal("ARCHIVED_DEPARTMENT_FOUND"),
  message: nonEmptyString,
  data: z.object({
    id: nonEmptyString,
    nameEn: nonEmptyString,
    nameAr: nonEmptyString,
    accountId: z.string().nullable().optional(),
    deletedAt: nonEmptyString,
  }),
});

export type CurrencyDto = z.infer<typeof currencyDtoSchema>;
export type DepartmentDto = z.infer<typeof departmentDtoSchema>;
export type ArchivedDepartmentDto = z.infer<typeof archivedDepartmentDtoSchema>;
export type DepartmentCreateWriteDto = z.infer<
  typeof departmentCreateWriteDtoSchema
>;
export type DepartmentUpdateWriteDto = z.infer<
  typeof departmentUpdateWriteDtoSchema
>;
export type ArchivedDepartmentMatchDto = z.infer<
  typeof archivedDepartmentMatchResponseDtoSchema
>["data"];
export type BranchDto = z.infer<typeof branchDtoSchema>;
export type BranchCreateWriteDto = z.infer<typeof branchCreateWriteDtoSchema>;
export type BranchUpdateWriteDto = z.infer<typeof branchUpdateWriteDtoSchema>;
export type ArchivedBranchListItemDto = z.infer<
  typeof archivedBranchListItemDtoSchema
>;
export type ArchivedBranchMatchDto = z.infer<typeof archivedBranchMatchDtoSchema>;
export type CountryDto = z.infer<typeof countryDtoSchema>;
export type GovernorateDto = z.infer<typeof governorateDtoSchema>;
export type CompanyDto = z.infer<typeof companyDtoSchema>;
export type ArchivedCompanyListItemDto = z.infer<
  typeof archivedCompanyListItemDtoSchema
>;
export type ArchivedCompanyMatchDto = z.infer<
  typeof archivedCompanyMatchDtoSchema
>;
export type CompanySummaryDto = z.infer<typeof companySummaryDtoSchema>;
export type AccountAdminDto = z.infer<typeof accountAdminDtoSchema>;
export type ArchivedAdminListItemDto = z.infer<
  typeof archivedAdminListItemDtoSchema
>;
export type CompanyCreateWriteDto = z.infer<typeof companyCreateWriteDtoSchema>;
export type CompanyUpdateWriteDto = z.infer<typeof companyUpdateWriteDtoSchema>;
export type AccountAdminCreateWriteDto = z.infer<
  typeof accountAdminCreateWriteDtoSchema
>;
export type AccountAdminUpdateWriteDto = z.infer<
  typeof accountAdminUpdateWriteDtoSchema
>;
export type RegionDto = z.infer<typeof regionDtoSchema>;
export type RegionCreateWriteDto = z.infer<typeof regionCreateWriteDtoSchema>;
export type RegionUpdateWriteDto = z.infer<typeof regionUpdateWriteDtoSchema>;
export type ArchivedRegionDto = z.infer<typeof archivedRegionDtoSchema>;
export type ArchivedRegionMatchDto = z.infer<typeof archivedRegionMatchDtoSchema>;
